import './ffs-parameter-input.css'

import {
  CheckCircleTwoTone,
  InfoCircleOutlined,
  StopTwoTone,
  TableOutlined,
} from '@ant-design/icons'
import { Button, ConfigProvider, InputNumber, Tooltip, Typography } from 'antd'
import { FFS_DISPLAY_DECIMALS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useGeckoLocale } from 'features/locale'
import isNil from 'lodash/isNil'
import React, {
  CSSProperties,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { LABEL_WIDTH } from './FfsCalcLayout'

export type MeasurementType =
  | 'thickness'
  | 'ratio'
  | 'pressure'
  | 'temperature'
  | 'length'
  | 'rate'
  | 'time'

const { Text } = Typography

const ICON_STYLE: React.CSSProperties = {
  fontSize: '1.5rem',
}

const RenderAfterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  height: 100%;
  flex-wrap: nowrap;
`

const UNITS: Partial<Record<MeasurementType, readonly GeckoMeasurementUnit[]>> =
  {
    thickness: [
      GeckoMeasurementUnit.Inches,
      GeckoMeasurementUnit.Feet,
      GeckoMeasurementUnit.Millimeters,
      GeckoMeasurementUnit.Meters,
    ],
    pressure: [
      GeckoMeasurementUnit.Megapascals,
      GeckoMeasurementUnit.PoundsForcePerSqInch,
    ],
    temperature: [
      GeckoMeasurementUnit.Fahrenheit,
      GeckoMeasurementUnit.Celsius,
    ],
    length: [
      GeckoMeasurementUnit.Millimeters,
      GeckoMeasurementUnit.Inches,
      GeckoMeasurementUnit.Feet,
      GeckoMeasurementUnit.Meters,
    ],
    rate: [
      GeckoMeasurementUnit.MillimetersPerYear,
      GeckoMeasurementUnit.InchesPerYear,
    ],
    time: [GeckoMeasurementUnit.Years],
    // Dimensionless. Listing Ratio explicitly keeps the global-unit derivation
    // uniform: lookUpPreferredUnit(Ratio, system) returns Ratio unchanged, so
    // ratio fields never fall through to a length unit (e.g. after being cleared).
    ratio: [GeckoMeasurementUnit.Ratio],
  }

export interface FfsParameterInputFieldProps {
  label: ReactNode
  labelTooltip?: ReactNode
  measurementType?: MeasurementType
  labelWidth?: CSSProperties['width']
  value: GeckoMeasurement | null
  setValue?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  isValid?: boolean
  min?: GeckoMeasurement
  max?: GeckoMeasurement
  step?: number
  trailingText?:
    | string
    | ((value: GeckoMeasurement | null) => string | undefined)
  onLookup?: () => void
  onInfo?: () => void
  /**
   * Small secondary line under the field naming where the value came from
   * (e.g. "From asset model", "Overridden (asset model: 150.00 psi)", "From
   * scenario"). Omit or pass `undefined` to render nothing.
   */
  caption?: ReactNode
}

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: left;
  width: 100%;
  aligh-items: center;
  gap: 1rem;
`

const CaptionText = styled(Text)`
  font-size: 0.7rem;
  color: ${CantileverTheme.colors.grayscale[300]};
`

const RawFfsParameterField: FC<FfsParameterInputFieldProps> = ({
  label,
  labelTooltip,
  measurementType = 'thickness',
  labelWidth = LABEL_WIDTH,
  value,
  setValue,
  isValid,
  min,
  max,
  step,
  onLookup,
  onInfo,
  trailingText,
  caption,
}) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const allowableUnits = useMemo(
    () => UNITS[measurementType] ?? [],
    [measurementType]
  )
  // Display unit follows the single global measurement system — no per-field
  // override. Ratio (dimensionless) resolves to itself, so this is uniform for
  // every field type. Falls back to null only if a type has no allowable units,
  // in which case display/edit use the value's own unit.
  const unit = useMemo(
    () =>
      allowableUnits.length
        ? getPreferredMeasurementUnit(allowableUnits[0])
        : null,
    [allowableUnits, getPreferredMeasurementUnit]
  )

  const infoAfter = useMemo(
    () =>
      onInfo && (
        <Button
          shape='circle'
          icon={<InfoCircleOutlined />}
          onClick={onInfo}
          size='small'
          type='text'
        />
      ),
    [onInfo]
  )

  const lookupAfter = useMemo(
    () =>
      onLookup && (
        <Button
          shape='circle'
          icon={<TableOutlined />}
          onClick={onLookup}
          size='small'
          type='text'
        />
      ),
    [onLookup]
  )
  // Static unit label (replaces the old per-field unit <Select>). Suppressed for
  // dimensionless fields so ratios don't show a "Ratio"/"None" suffix.
  const unitSuffix = useMemo(() => {
    const activeUnit = unit ?? value?.unit ?? null
    if (
      !activeUnit ||
      activeUnit === GeckoMeasurementUnit.Ratio ||
      activeUnit === GeckoMeasurementUnit.None
    ) {
      return undefined
    }
    return (
      <Text
        style={{
          color: CantileverTheme.colors.grayscale[300],
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
          minWidth: 40,
          textAlign: 'center',
        }}>
        {activeUnit.name}
      </Text>
    )
  }, [unit, value])

  const trailingTextVal = useMemo(
    () =>
      typeof trailingText === 'function'
        ? trailingText(value?.convertTo(unit ?? value.unit) ?? null)
        : trailingText,
    [trailingText, unit, value]
  )

  const renderAfter = useMemo(() => {
    if (!infoAfter && !lookupAfter && !unitSuffix && !trailingTextVal) {
      return undefined
    }
    return (
      <RenderAfterContainer>
        {trailingTextVal ? (
          <Text
            style={{
              color: CantileverTheme.colors.grayscale[300],
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
              marginLeft: '0.25rem',
              marginRight: '0.25rem',
            }}>
            {trailingTextVal}
          </Text>
        ) : undefined}
        {infoAfter ? infoAfter : undefined}
        {lookupAfter ? lookupAfter : undefined}
        {unitSuffix ? unitSuffix : undefined}
      </RenderAfterContainer>
    )
  }, [infoAfter, lookupAfter, unitSuffix, trailingTextVal])

  const checkNewValue = useCallback(
    (value: GeckoMeasurement | null) => {
      if (value === null) {
        return true
      }
      if (min && min.convertTo(value.unit).num > value.num) {
        return false
      }
      if (max && max.convertTo(value.unit).num < value.num) {
        return false
      }
      return true
    },
    [max, min]
  )

  return (
    <ConfigProvider
      theme={{
        components: {
          InputNumber: {
            colorBgContainer:
              setValue || onLookup
                ? CantileverTheme.colors.grayscale[600]
                : CantileverTheme.colors.grayscale[700],
          },
        },
      }}>
      <FieldWrapper>
        <Container>
          <Tooltip title={labelTooltip}>
            <Text style={{ width: labelWidth ?? '30%' }}>{label}</Text>
          </Tooltip>
          <InputNumber
            style={{ width: '70%' }}
            value={
              isNil(value)
                ? null
                : +value
                    ?.convertTo(unit ?? value.unit)
                    .num.toFixed(FFS_DISPLAY_DECIMALS)
            }
            onChange={(num) => {
              if (!setValue) {
                return
              }
              const newValue =
                num === null
                  ? null
                  : GeckoMeasurement.CreateFromValue(
                      num,
                      unit ?? value?.unit ?? GeckoMeasurementUnit.Millimeters
                    )
              if (checkNewValue(newValue)) {
                setValue(newValue)
              }
            }}
            readOnly={!setValue}
            addonAfter={renderAfter}
            status={isValid === false ? 'error' : undefined}
            step={step}
          />
          {isValid !== undefined && (
            <>
              {isValid ? (
                <CheckCircleTwoTone twoToneColor={'green'} style={ICON_STYLE} />
              ) : (
                <StopTwoTone twoToneColor={'red'} style={ICON_STYLE} />
              )}
            </>
          )}
        </Container>
        {caption && (
          <CaptionText style={{ marginLeft: labelWidth ?? '30%' }}>
            {caption}
          </CaptionText>
        )}
      </FieldWrapper>
    </ConfigProvider>
  )
}
RawFfsParameterField.displayName = 'FfsParameterField'

export const FfsParameterField = React.memo(RawFfsParameterField)
